/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { useIntl } from 'react-intl'

import {
  EndpointsPanel,
  EndpointsPanelLink,
} from '@modules/resources-shared-components/EndpointsPanel/EndpointsPanel'
import { useFlagsWhenLoaded } from '@modules/launchdarkly'
import type { StackDeployment } from '@modules/ui-types'

import { gte } from '@/lib/semver'
import { getVersion } from '@/lib/stackDeployments/selectors/fundamentals'
import { securityUrl } from '@/lib/urlBuilder'

import { getLinks } from '../../../lib/deployments/links'

interface DeploymentEndpointsPanelProps {
  deployment: StackDeployment
}

export const DeploymentEndpointsPanel = (props: DeploymentEndpointsPanelProps) => {
  const { deployment } = props

  const { formatMessage } = useIntl()

  const version = getVersion({ deployment }) || ''
  const isEnterpriseSearchDeprecated = version ? gte(version, '9.0.0') : false
  const [_, ldFlags] = useFlagsWhenLoaded()
  const resetPasswordUrl = securityUrl(deployment.id)

  const links = getLinks({
    deployment,
    showMotelTechPreviewLabel: ldFlags.showMotelTechPreviewLabelEch,
  }).filter((link) => !(link.appKey === 'enterprise_search' && isEnterpriseSearchDeprecated))

  const getFormattedLinks = (): EndpointsPanelLink[] => {
    const formattedLinks: EndpointsPanelLink[] = []

    links.forEach((link) => {
      if (!link.appKey) {
        return
      }

      const label = formatMessage(link.label)
      formattedLinks.push({
        isEnabled: link.available && !!link.apiUri,
        showOpenButton: link.showLaunchLink,
        label,
        appType: link.appKey,
        url: link.apiUri,
        openUrl: link.uiUri,
        id: link.appID,
        buttonDataTestId: `application-link-${link.id}`,
      })
    })

    return formattedLinks
  }

  return <EndpointsPanel links={getFormattedLinks()} resetPasswordUrl={resetPasswordUrl} />
}
